/**
 * License Details Mock Data
 * Contains dummy data for individual license details
 * This data will be replaced with real API responses when backend is ready
 */

import type { LicenseDetail } from '../components/Dashboard/LicenseDetailsPanel/type';

const LICENSE_DETAILS_MOCK_DATA: Record<string, LicenseDetail | undefined> = {
  '1': {
    id: '1',
    vendor: 'SAP',
    logo: 'SAP',
    totalSpend: '$4.4M',
    spendTrend: '+3.6%',
    spendTrendDirection: 'up',
    utilization: '81%',
    utilizationTrend: '+2.7%',
    utilizationTrendDirection: 'up',
    costPerEmployee: '$2,874',
    costTrend: '+3.2%',
    costTrendDirection: 'up',
    optimizationScore: '83%',
    optimizationTrend: '-1.2%',
    optimizationTrendDirection: 'down',
    riskAlerts: '1',
    riskLevel: '1 Medium',
    sku: 'SAP-RISE-FUE',
    renewalDate: 'Exp Date 04/22/2025',
    userRoles: ['310 Managers', '1000 Users', '220 Developers'],
    licenseCost: 'Total Annual Cost: $4.4M',
    usage: '1215/1,530',
    usagePercentage: 79,
    status: 'Active',
    findings:
      'You can save $144,000 on your renewal by adjusting role classifications. I can build a report and share with you.',
  },
  '2': {
    id: '2',
    vendor: 'Office 365',
    logo: 'Office 365',
    totalSpend: '$2.8M',
    spendTrend: '+1.2%',
    spendTrendDirection: 'up',
    utilization: '74%',
    utilizationTrend: '+4.1%',
    utilizationTrendDirection: 'up',
    costPerEmployee: '$1,245',
    costTrend: '+0.8%',
    costTrendDirection: 'up',
    optimizationScore: '76%',
    optimizationTrend: '+2.3%',
    optimizationTrendDirection: 'up',
    riskAlerts: '0',
    riskLevel: 'No Risk',
    sku: 'O365-E5-2026',
    renewalDate: 'Exp Date 06/15/2026',
    userRoles: ['2500 Users', '450 Admins', '150 Developers'],
    licenseCost: 'Total Annual Cost: $2.8M',
    usage: '2250/3000',
    usagePercentage: 75,
    status: 'Active',
    findings:
      'Consider downgrading some users from E5 to E3 licenses to reduce costs by approximately $320,000 annually.',
  },
  '3': {
    id: '3',
    vendor: 'AWS',
    logo: 'AWS',
    totalSpend: '$3.2M',
    spendTrend: '+5.8%',
    spendTrendDirection: 'up',
    utilization: '68%',
    utilizationTrend: '-1.5%',
    utilizationTrendDirection: 'down',
    costPerEmployee: '$1,856',
    costTrend: '+5.2%',
    costTrendDirection: 'up',
    optimizationScore: '62%',
    optimizationTrend: '-3.1%',
    optimizationTrendDirection: 'down',
    riskAlerts: '2',
    riskLevel: '2 High',
    sku: 'AWS-ENTERPRISE-2026',
    renewalDate: 'Exp Date 03/10/2026',
    userRoles: ['180 Developers', '45 DevOps', '25 Architects'],
    licenseCost: 'Total Annual Cost: $3.2M',
    usage: '850/1250',
    usagePercentage: 68,
    status: 'Active',
    findings:
      'Reserved instances could save you $480,000 annually. We recommend committing to 1-year or 3-year plans for predictable workloads.',
  },
  '4': {
    id: '4',
    vendor: 'Workday',
    logo: 'Workday',
    totalSpend: '$3.2M',
    spendTrend: '+2.1%',
    spendTrendDirection: 'up',
    utilization: '76%',
    utilizationTrend: '+1.8%',
    utilizationTrendDirection: 'up',
    costPerEmployee: '$2,105',
    costTrend: '+1.5%',
    costTrendDirection: 'up',
    optimizationScore: '79%',
    optimizationTrend: '+0.9%',
    optimizationTrendDirection: 'up',
    riskAlerts: '1',
    riskLevel: '1 Medium',
    sku: 'WORKDAY-HCM-2026',
    renewalDate: 'Exp Date 03/15/2026',
    userRoles: ['1200 Employees', '85 Managers', '40 Admins'],
    licenseCost: 'Total Annual Cost: $3.2M',
    usage: '1520/2000',
    usagePercentage: 76,
    status: 'Active',
    findings:
      'Optimize your Workday modules by consolidating unused add-ons. Potential savings: $240,000 annually.',
  },
  '5': {
    id: '5',
    vendor: 'Salesforce',
    logo: 'Salesforce',
    totalSpend: '$5.1M',
    spendTrend: '+4.2%',
    spendTrendDirection: 'up',
    utilization: '76%',
    utilizationTrend: '+3.5%',
    utilizationTrendDirection: 'up',
    costPerEmployee: '$2,550',
    costTrend: '+3.8%',
    costTrendDirection: 'up',
    optimizationScore: '81%',
    optimizationTrend: '+1.6%',
    optimizationTrendDirection: 'up',
    riskAlerts: '0',
    riskLevel: 'No Risk',
    sku: 'SFDC-UNLIMITED-2026',
    renewalDate: 'Exp Date 04/20/2026',
    userRoles: ['1500 Sales Users', '300 Service Users', '200 Admins'],
    licenseCost: 'Total Annual Cost: $5.1M',
    usage: '1890/2500',
    usagePercentage: 76,
    status: 'Active',
    findings:
      'Review inactive user licenses and consider moving to lower-tier editions. Estimated savings: $380,000 per year.',
  },
  '6': {
    id: '6',
    vendor: 'Saviynt',
    logo: 'Saviynt',
    totalSpend: '$2.8M',
    spendTrend: '+1.5%',
    spendTrendDirection: 'up',
    utilization: '76%',
    utilizationTrend: '+2.2%',
    utilizationTrendDirection: 'up',
    costPerEmployee: '$1,400',
    costTrend: '+0.9%',
    costTrendDirection: 'up',
    optimizationScore: '88%',
    optimizationTrend: '+2.1%',
    optimizationTrendDirection: 'up',
    riskAlerts: '0',
    riskLevel: 'No Risk',
    sku: 'SAVIYNT-IDENTITY-2026',
    renewalDate: 'Exp Date 05/10/2026',
    userRoles: ['2000 Managed Users', '150 Admins', '50 Auditors'],
    licenseCost: 'Total Annual Cost: $2.8M',
    usage: '1520/2000',
    usagePercentage: 76,
    status: 'Active',
    findings:
      'Your Saviynt implementation is well-optimized. Continue monitoring user growth to ensure license alignment.',
  },
};

export default LICENSE_DETAILS_MOCK_DATA;
